import {
  epochSecondsOf,
  instantFromEpochSeconds,
} from "../../../../../../../../utils/temporal";
import { useRootStore } from "../../../../../../../../stores/hooks";
import type { DayOfWeek } from "@smartsuite/automation-engine-api";
import { Temporal } from "@js-temporal/polyfill";
import { toJS } from "mobx";

interface ScheduleFunctions {
  getScheduleType: () => string;
  getOldScheduleType: () => string;
  setScheduleType: (type: string) => void;
  storeDefaultSchedule: () => void;
  getStartFromDate: () => number;
  setStartFromDate: (time: number) => void;
  getStartFromTime: () => number;
  setStartFromTime: (time: number) => void;
  getDaysOfWeek: () => DayOfWeek[];
  setDaysOfWeek: (days: DayOfWeek[]) => void;
  getRepeatEvery: () => number;
  setRepeatEvery: (every: number) => void;
  getMonthDays: () => number[];
  setMonthDays: (days: number[]) => void;
}

const DEFAULT_REPEAT_EVERY = 1;

export const useRepeatedSchedule = (): ScheduleFunctions => {
  const { scheduleStore, automations } = useRootStore();

  const getTimezone = (): string => automations.candidate.getTimezone();

  const getEpochSeconds = (): number =>
    +(scheduleStore.schedule?.initialDueDate?.seconds ?? "0");

  const getScheduleType = (): string => {
    if (scheduleStore.schedule?.schedule.oneofKind === "doNotRepeat") {
      return "doNotRepeat";
    } else if (scheduleStore.schedule?.schedule.oneofKind === "custom") {
      return scheduleStore.schedule.schedule.custom.unit.oneofKind ?? "weekly";
    }
    return "weekly";
  };

  const getOldScheduleType = (): string => {
    const type = getScheduleType();
    const types: { [key: string]: string } = {
      hourly: "hours",
      daily: "days",
      weekly: "weeks",
      monthly: "months",
      doNotRepeat: "one-time",
    };
    return types[type] ?? "";
  };

  const setScheduleType = (intervalType: string): void => {
    let everyN = 1;
    if (scheduleStore.schedule?.schedule.oneofKind === "custom") {
      everyN = scheduleStore.schedule.schedule.custom.everyN;
    } else if (scheduleStore.schedule?.schedule.oneofKind !== "doNotRepeat") {
      return;
    }

    let newSchedule = scheduleStore.schedule;

    if (intervalType === "one-time") {
      newSchedule = {
        ...newSchedule,
        schedule: {
          oneofKind: "doNotRepeat",
          doNotRepeat: {},
        },
      };
    } else if (intervalType === "hours") {
      newSchedule = {
        ...newSchedule,
        schedule: {
          oneofKind: "custom",
          custom: {
            everyN,
            unit: {
              oneofKind: "hourly",
              hourly: {},
            },
          },
        },
      };
    } else if (intervalType === "days") {
      newSchedule = {
        ...newSchedule,
        schedule: {
          oneofKind: "custom",
          custom: {
            everyN,
            unit: {
              oneofKind: "daily",
              daily: {},
            },
          },
        },
      };
    } else if (intervalType === "weeks") {
      newSchedule = {
        ...newSchedule,
        schedule: {
          oneofKind: "custom",
          custom: {
            everyN,
            unit: {
              oneofKind: "weekly",
              weekly: { daysOfWeek: getDaysOfWeek() },
            },
          },
        },
      };
    } else if (intervalType === "months") {
      newSchedule = {
        ...newSchedule,
        schedule: {
          oneofKind: "custom",
          custom: {
            everyN,
            unit: {
              oneofKind: "monthly",
              monthly: {
                daysOfMonth: getMonthDays(),
                lastDayOfMonth: false,
              },
            },
          },
        },
      };
    }
    scheduleStore.setSchedule(toJS(newSchedule));
  };

  const getStartFromDate = (): number => {
    const seconds = +(scheduleStore.schedule?.initialDueDate?.seconds ?? "0");
    return epochSecondsOf(
      instantFromEpochSeconds(seconds)
        .toZonedDateTimeISO(getTimezone())
        .with({ hour: 0, minute: 0, second: 0 }),
    );
  };

  const setStartFromDate = (date: number): void => {
    if (
      scheduleStore.schedule === undefined ||
      scheduleStore.schedule.initialDueDate === undefined
    ) {
      return;
    }
    const time = getStartFromTime();
    const seconds = epochSecondsOf(
      instantFromEpochSeconds(date)
        .toZonedDateTimeISO(getTimezone())
        .with({
          hour: (time / 60 / 60) | 0,
          minute: (time / 60) % 60,
        }),
    );

    scheduleStore.schedule.initialDueDate.seconds = String(seconds);
    scheduleStore.setSchedule(toJS(scheduleStore.schedule));
  };

  const getStartFromTime = (): number => {
    const time = instantFromEpochSeconds(getEpochSeconds()).toZonedDateTimeISO(
      getTimezone(),
    );

    return (time.hour * 60 + time.minute) * 60;
  };

  const setStartFromTime = (time: number): void => {
    if (
      scheduleStore.schedule === undefined ||
      scheduleStore.schedule.initialDueDate === undefined
    ) {
      return;
    }
    const seconds = epochSecondsOf(
      instantFromEpochSeconds(getEpochSeconds())
        .toZonedDateTimeISO(getTimezone())
        .with({
          hour: (time / 60 / 60) | 0,
          minute: (time / 60) % 60,
        }),
    );

    scheduleStore.schedule.initialDueDate.seconds = String(seconds);
    scheduleStore.setSchedule(toJS(scheduleStore.schedule));
  };

  const storeDefaultSchedule = (): void => {
    scheduleStore.setSchedule({
      initialDueDate: {
        seconds: String(
          epochSecondsOf(
            Temporal.Now.zonedDateTimeISO(getTimezone())
              .with({ hour: 9, minute: 0, second: 0 })
              .toInstant(),
          ),
        ),
        nanos: 0,
      },

      schedule: {
        oneofKind: "custom",
        custom: {
          everyN: 1,
          unit: {
            oneofKind: "weekly",
            weekly: {
              daysOfWeek: [
                instantFromEpochSeconds(getEpochSeconds()).toZonedDateTimeISO(
                  getTimezone(),
                ).dayOfWeek,
              ],
            },
          },
        },
      },
    });
  };

  const getDaysOfWeek = (): DayOfWeek[] => {
    if (
      scheduleStore.schedule?.schedule.oneofKind === "custom" &&
      scheduleStore.schedule.schedule.custom.unit.oneofKind === "weekly" &&
      scheduleStore.schedule.schedule.custom.unit.weekly.daysOfWeek.length > 0
    ) {
      return scheduleStore.schedule.schedule.custom.unit.weekly.daysOfWeek;
    }

    return [
      instantFromEpochSeconds(getEpochSeconds()).toZonedDateTimeISO(
        getTimezone(),
      ).dayOfWeek,
    ];
  };

  const setDaysOfWeek = (days: DayOfWeek[]): void => {
    if (
      scheduleStore.schedule?.schedule.oneofKind !== "custom" ||
      scheduleStore.schedule?.schedule.custom.unit.oneofKind !== "weekly"
    ) {
      return;
    }

    if (days.length === 0) {
      scheduleStore.schedule.schedule.custom.unit.weekly.daysOfWeek = [
        instantFromEpochSeconds(getEpochSeconds()).toZonedDateTimeISO(
          getTimezone(),
        ).dayOfWeek,
      ];
    } else {
      scheduleStore.schedule.schedule.custom.unit.weekly.daysOfWeek = days;
    }

    scheduleStore.setSchedule(toJS(scheduleStore.schedule));
  };

  const getRepeatEvery = (): number => {
    if (scheduleStore.schedule?.schedule.oneofKind === "custom") {
      return scheduleStore.schedule.schedule.custom.everyN;
    }
    return DEFAULT_REPEAT_EVERY;
  };

  const setRepeatEvery = (every: number): void => {
    if (scheduleStore.schedule?.schedule.oneofKind !== "custom") {
      return;
    }
    if (every <= 0) {
      return;
    }
    scheduleStore.schedule.schedule.custom.everyN = every;
    scheduleStore.setSchedule(toJS(scheduleStore.schedule));
  };

  const getMonthDays = (): number[] => {
    if (
      scheduleStore.schedule?.schedule.oneofKind !== "custom" ||
      scheduleStore.schedule.schedule.custom.unit.oneofKind !== "monthly"
    ) {
      return [Temporal.Now.zonedDateTimeISO(getTimezone()).day];
    }

    return [
      ...scheduleStore.schedule.schedule.custom.unit.monthly.daysOfMonth,
      ...(scheduleStore.schedule.schedule.custom.unit.monthly.lastDayOfMonth
        ? [-1]
        : []),
    ];
  };

  const setMonthDays = (days: number[]): void => {
    if (
      scheduleStore.schedule?.schedule.oneofKind !== "custom" ||
      scheduleStore.schedule.schedule.custom.unit.oneofKind !== "monthly"
    ) {
      return;
    }

    let newDays = days.filter((day) => day !== -1);
    if (days.length === 0) {
      newDays = [Temporal.Now.zonedDateTimeISO(getTimezone()).day];
    }
    scheduleStore.schedule.schedule.custom.unit.monthly = {
      daysOfMonth: newDays,
      lastDayOfMonth: days.find((day) => day === -1) !== undefined,
    };
    scheduleStore.setSchedule(toJS(scheduleStore.schedule));
  };

  return {
    getScheduleType,
    getOldScheduleType,
    setScheduleType,
    storeDefaultSchedule,
    getStartFromDate,
    setStartFromDate,
    getStartFromTime,
    setStartFromTime,
    getDaysOfWeek,
    setDaysOfWeek,
    getRepeatEvery,
    setRepeatEvery,
    getMonthDays,
    setMonthDays,
  };
};
